import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import '../styles/Admin.css';

function formatBytes(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function Admin() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('vpn');
  const [vpnStatus, setVpnStatus] = useState({ enabled: false, configExists: false, config: null });
  const [selectedFile, setSelectedFile] = useState(null);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editStorageLimit, setEditStorageLimit] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchVpnStatus();
    fetchUsers();
    fetchStats();
  }, []);

  const fetchVpnStatus = async () => {
    try {
      const res = await fetch('/api/admin/wireguard/status', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setVpnStatus(data);
      }
    } catch (err) {
      console.error('Failed to fetch VPN status:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await fetch('/api/admin/users', { credentials: 'include' });
      if (res.ok) setUsers(await res.json());
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch('/api/admin/stats', { credentials: 'include' });
      if (res.ok) setStats(await res.json());
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.name.endsWith('.conf')) {
      setSelectedFile(file);
    } else {
      showMessage('Please select a valid .conf file', 'error');
    }
  };

  const uploadConfig = async () => {
    if (!selectedFile) {
      showMessage('Please select a config file', 'error');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('config', selectedFile);

    try {
      const res = await fetch('/api/admin/wireguard/config', {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const data = await res.json();
      if (res.ok) {
        showMessage('Config uploaded successfully', 'success');
        setSelectedFile(null);
        fetchVpnStatus();
      } else {
        showMessage(data.error || 'Failed to upload config', 'error');
      }
    } catch (err) {
      showMessage('Network error', 'error');
    } finally {
      setLoading(false);
    }
  };

  const toggleVpn = async () => {
    setLoading(true);
    const endpoint = vpnStatus.enabled ? '/api/admin/wireguard/disable' : '/api/admin/wireguard/enable';
    
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await res.json();
      if (res.ok) {
        showMessage(data.message, 'success');
        fetchVpnStatus();
      } else {
        showMessage(data.message || 'Failed to toggle VPN', 'error');
      }
    } catch (err) {
      showMessage('Network error', 'error');
    } finally {
      setLoading(false);
    }
  };

  const deleteConfig = async () => {
    if (!confirm('Delete VPN configuration?')) return;
    
    try {
      const res = await fetch('/api/admin/wireguard/config', {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        showMessage('Config deleted', 'success');
        fetchVpnStatus();
      }
    } catch (err) {
      showMessage('Failed to delete config', 'error');
    }
  };

  const updateUserStorage = async (userId) => {
    const limit = parseInt(editStorageLimit);
    if (!limit || limit < 1) return;
    
    try {
      const res = await fetch(`/api/admin/users/${userId}/storage`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ storage_limit_mb: limit })
      });

      if (res.ok) {
        showMessage('Storage limit updated', 'success');
        setEditingUser(null);
        fetchUsers();
      }
    } catch (err) {
      showMessage('Network error', 'error');
    }
  };

  const deleteUser = async (userId) => {
    if (!confirm('Delete this user? All data will be lost.')) return;
    
    try {
      const res = await fetch(`/api/admin/users/${userId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        showMessage('User deleted', 'success');
        fetchUsers();
      }
    } catch (err) {
      showMessage('Network error', 'error');
    }
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  return (
    <div className="admin-panel">
      <header className="admin-header">
        <div className="admin-title">
          <h1>ADMIN</h1>
          <span>System Configuration</span>
        </div>
        <div className="admin-actions">
          <button className="terminal-btn secondary" onClick={() => navigate('/')}>
            Back to App
          </button>
          <button className="terminal-btn" onClick={logout}>
            Logout
          </button>
        </div>
      </header>

      <nav className="admin-nav">
        <button 
          className={`nav-item ${activeTab === 'vpn' ? 'active' : ''}`} 
          onClick={() => setActiveTab('vpn')}
        >
          VPN
        </button>
        <button 
          className={`nav-item ${activeTab === 'users' ? 'active' : ''}`} 
          onClick={() => setActiveTab('users')}
        >
          Users
        </button>
        <button 
          className={`nav-item ${activeTab === 'stats' ? 'active' : ''}`} 
          onClick={() => setActiveTab('stats')}
        >
          Statistics
        </button>
      </nav>

      <main className="admin-content">
        {message && (
          <div className={`terminal-message ${message.type}`}>{message.text}</div>
        )}

        {activeTab === 'vpn' && (
          <section className="admin-section">
            <div className="section-header">
              <h2>WireGuard VPN</h2>
              <span className="number">01</span>
            </div>
            
            <div className="terminal-form">
              {/* VPN Status */}
              <div className="vpn-status">
                <div className="terminal-label">
                  <span>Status</span>
                  <div className={`status-badge ${vpnStatus.enabled ? 'active' : 'inactive'}`}>
                    {vpnStatus.enabled ? 'Enabled' : 'Disabled'}
                  </div>
                </div>
              </div>

              {/* Config Upload */}
              {!vpnStatus.configExists ? (
                <div className="config-upload">
                  <label className="terminal-label">
                    <span>Config File</span>
                    <div className="file-input-wrapper">
                      <input
                        type="file"
                        accept=".conf"
                        onChange={handleFileChange}
                        className="file-input"
                      />
                      <span className="file-name">
                        {selectedFile ? selectedFile.name : 'Select .conf file'}
                      </span>
                    </div>
                  </label>
                  {selectedFile && (
                    <div className="terminal-actions">
                      <button className="terminal-btn" onClick={uploadConfig} disabled={loading}>
                        {loading ? 'Uploading...' : 'Upload Config'}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="config-info">
                  <label className="terminal-label">
                    <span>Configuration</span>
                    <div className="config-details">
                      {vpnStatus.config && (
                        <>
                          <div className="config-item">
                            <span className="config-label">File:</span>
                            <span className="config-value">{vpnStatus.config.name}</span>
                          </div>
                          <div className="config-item">
                            <span className="config-label">Endpoint:</span>
                            <span className="config-value">{vpnStatus.config.endpoint}</span>
                          </div>
                          <div className="config-item">
                            <span className="config-label">Address:</span>
                            <span className="config-value">{vpnStatus.config.address}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </label>
                  
                  <div className="terminal-actions">
                    <button 
                      className={`terminal-btn ${vpnStatus.enabled ? 'danger' : ''}`} 
                      onClick={toggleVpn}
                      disabled={loading}
                    >
                      {loading ? 'Processing...' : (vpnStatus.enabled ? 'Disable VPN' : 'Enable VPN')}
                    </button>
                    <button className="terminal-btn danger" onClick={deleteConfig}>
                      Delete Config
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {activeTab === 'users' && (
          <section className="admin-section">
            <div className="section-header">
              <h2>User Management</h2>
              <span className="number">02</span>
            </div>
            <div className="terminal-table-container">
              <table className="terminal-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Username</th>
                    <th>Admin</th>
                    <th>Storage Used</th>
                    <th>Storage Limit</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id}>
                      <td>{u.id}</td>
                      <td>{u.username}</td>
                      <td>{u.is_admin ? 'Yes' : 'No'}</td>
                      <td>{formatBytes(u.storage_used_bytes)}</td>
                      <td>
                        {editingUser === u.id ? (
                          <input
                            type="number"
                            value={editStorageLimit}
                            onChange={(e) => setEditStorageLimit(e.target.value)}
                            className="terminal-input small"
                          />
                        ) : (
                          `${u.storage_limit_mb} MB`
                        )}
                      </td>
                      <td>
                        {editingUser === u.id ? (
                          <>
                            <button className="terminal-btn small" onClick={() => updateUserStorage(u.id)}>
                              Save
                            </button>
                            <button className="terminal-btn small secondary" onClick={() => setEditingUser(null)}>
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button 
                              className="terminal-btn small secondary" 
                              onClick={() => { setEditingUser(u.id); setEditStorageLimit(u.storage_limit_mb); }}
                            >
                              Edit
                            </button>
                            {!u.is_admin && (
                              <button className="terminal-btn small danger" onClick={() => deleteUser(u.id)}>
                                Delete
                              </button>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {activeTab === 'stats' && stats && (
          <section className="admin-section">
            <div className="section-header">
              <h2>System Statistics</h2>
              <span className="number">03</span>
            </div>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-value">{stats.total_users}</div>
                <div className="stat-label">Total Users</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{stats.total_downloads}</div>
                <div className="stat-label">Downloads</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{formatBytes(stats.total_storage_bytes)}</div>
                <div className="stat-label">Total Storage</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{formatBytes(stats.compression_savings_bytes)}</div>
                <div className="stat-label">Space Saved</div>
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

export default Admin;